// Hook: al montar el elemento (o cuando cambia una clave de activación), lleva
// el foco y el scroll de la página hacia él. Pensado para mensajes de error o
// advertencia que aparecen en la parte superior del contenido: si el usuario
// está desplazado hacia abajo, no los vería. Al enfocar + hacer scroll suave al
// mensaje, se entera sin tener que desplazarse manualmente.
//
// Accesibilidad: el elemento objetivo debe ser enfocable (p. ej. `tabIndex={-1}`)
// y, para lectores de pantalla, declarar `role="alert"`/`aria-live`. Enfocar un
// contenedor con `role="alert"` también mueve el "punto de lectura" del teclado
// al mensaje.

import { useEffect, useRef } from 'react';

/**
 * Devuelve una `ref` para adjuntar al contenedor del mensaje. Cuando el nodo
 * aparece (o cuando cambia alguno de los valores de `deps`, p. ej. el texto del
 * error), hace scroll del elemento al viewport y le da foco.
 *
 * @param active   Si el mensaje está visible. Solo actúa cuando es `true`.
 * @param deps     Valores que, al cambiar, re-disparan el enfoque (p. ej. el
 *                 mensaje en sí, para re-centrar ante un error distinto).
 */
export function useScrollFocusOnMount<T extends HTMLElement = HTMLDivElement>(
  active: boolean,
  deps: readonly unknown[] = [],
) {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    if (!active) return;
    const el = ref.current;
    if (!el) return;

    // Respeta la preferencia del usuario de reducir movimiento: si la tiene
    // activada, salta al elemento sin animación de scroll.
    const prefersReduced =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    try {
      el.scrollIntoView({
        behavior: prefersReduced ? 'auto' : 'smooth',
        block: 'center',
      });
    } catch {
      // Entornos sin scrollIntoView (o jsdom en pruebas): ignorar.
    }

    // Enfocar tras el scroll para no pelear con la animación en algunos
    // navegadores. `preventScroll` evita un segundo salto brusco; el scroll
    // suave ya lo hizo `scrollIntoView`.
    try {
      el.focus({ preventScroll: true });
    } catch {
      // Elemento no enfocable o entorno sin focus: ignorar.
    }
    // `deps` es variádico (spread del llamador): la regla no puede verificar
    // estáticamente un arreglo con spread, por eso se desactiva en esta línea.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, ...deps]);

  return ref;
}
